/**
 * deal-detail/types.ts — 딜 상세의 state 정의 + factory + 도메인 상수
 *
 * DealDetailView.ts 에서 분리 (정리 Step 1, 2026-07-03 — 분해 계획 v2 §2).
 * 이동만 — 동작 변화 없음. 이후 render-* 모듈들이 view 를 거치지 않고
 * 여기서 직접 import 하기 위한 공유 지점 (순환 import 방지).
 */

import * as api from '../../api/client';
import { escapeHtml } from '../../lib/cells';
import type { SourcePool } from '../../lib/source-options';
import type { RentalPreset } from '../../api/rental-preset-api';


// ─── 라우트 loader 계약 ─────────────────────────────────────────────
/** 라우트 loader 의 반환 타입. main.ts 의 loader 와 공유. */
export interface DealDetailLoaded {
  full:      api.DealFull;
  assignees: api.UserAssignee[];
  sourcing:  api.DealSourcingResponse;   // P9-UI Step 2 — need 평탄 리스트
  quotes:    api.DealQuotesResponse;     // P9-UI Step 3 — 견적 목록
}

// ─── 뷰 전체 state ──────────────────────────────────────────────────
export interface DealDetailData extends DealDetailLoaded {
  dealId: string;
  newMode: boolean;                // 딜 생성 모드(빈 드래프트) — 헤더/액션 다르게, 취소=삭제
  activeConfigId: string | null;   // B3 — 현재 보고 있는 구성 탭
  editConfig: EditConfigState | null;        // E2 — 구성 편집 드래프트
  itemSearch: ItemSearchState;               // E2 — 카탈로그 검색 패널
  contextCollapsed: { original: boolean; history: boolean };   // 메모 패널 접기
  statusPopoverOpen: boolean;                                  // 헤더 상태 트리거 팝오버 (STATUS UI)
  statusHistory: api.DealStatusHistoryEntry[] | null;          // 상태 전이 이력 (팝오버 열 때 로드)
  rfqViewBubble: api.SourcingRfqBubble | null;                 // 소싱 RFQ 버블 보기 모달 (6D)
  tagEditor: { needId: string; kind: 'sales' | 'memo' } | null;   // 영업요청/소싱메모 태그 편집 (6D)
  itemMenuId: string | null;                                      // 품목 행 작업(⋯) 메뉴 — 열린 deal_item_id
  // 서버 구성 V2 — 그룹 UI 상태 (2026-07-30)
  //   서버는 기본 접힘. 펼친 서버행 id 집합 + ⋯메뉴가 열린 서버행 id.
  expandedServerIds: Set<string>;
  serverMenuId: string | null;
  /**
   * 품목 소스 dropdown 후보(재고 + RFQ) — 서버 구성품과 개별 품목 공용.
   * 견적 모달이 쓰는 quoteSources 와 별개 — 그쪽엔 재고가 없다.
   */
  itemSources: SourcePool;
  quoteMenuId: string | null;                                     // 견적 행 작업(⋯) 메뉴 — 열린 quote id
  /** '견적 계산' ▼ 가 열려 있는가 (판매/임대 선택, 마이그 178). */
  quoteKindMenuOpen: boolean;
  needMenuId: string | null;                                      // 소싱 니즈 행 작업(⋯) 메뉴 — 열린 need id
  needCreate: {                                                   // 니즈 생성 모달 (6D)
    open:        boolean;
    query:       string;                  // 품목 검색어
    searching:   boolean;
    hits:        api.CatalogSearchHit[];
    option_part: string;                  // 선택된 품목
    item_text:   string;
    category:    string | null;
    condition:   string;
    quantity:    string;
    salesTags:   string[];
    dealLinked:  boolean;                 // 이 딜에 연결 여부 (체크박스로 기본값 결정)
    saving:      boolean;
    /**
     * 품목 행 ⋯ 에서 열었을 때 그 deal_item id. need_origin 링크로 보낸다.
     * 소싱 페이지식 순수 수동 생성(품목 검색부터 시작)은 null.
     */
    sourceDealItemId: string | null;
  };
  needEdit: NeedEditState;                                        // 니즈 수정 모달(수량·컨디션)
  /**
   * `+ 서버 추가` 모달 — 두 갈래를 먼저 고른다.
   *   신규 구성 만들기 : 별탭 편집기에서 Chassis 부터
   *   기존 구성 불러오기 : **다른 딜**의 서버를 이 구성으로 복사
   *     (같은 딜 안에서는 서버 ⋯ 메뉴의 '복제'가 그 역할을 한다)
   */
  serverAdd:      ServerAddState;
  quoteModal:     QuoteModalState;
  quoteSources:   api.DealRfqSource[];   // 견적 소스(딜 RFQ) 후보 — 모달 열 때 로드
  quotePrices:    api.DealQuotePrice[];  // 최근 견적 단가(발송된 견적) — deal-sources 와 함께 로드
  dealEvents:     api.DealEvent[];       // 하단 활동 히스토리
  customerModal:  DealCustomerModalState;    // 거래처 선택 모달 (2026-07-14 스펙)
  contactModal:   DealContactModalState;     // 담당자 선택 모달
  quoteViewModal: QuoteViewModalState;
  quoteDownloadModal: QuoteDownloadModalState;
  cancelModal:    DealCancelModalState;      // 딜 취소 확인 + 사유 선택 모달
  // 채팅 state (입력/첨부/pending) → components/ChatPanel.ts 소유로 이동 (Step 5).
}

// ─── 딜 취소 모달 state ─────────────────────────────────────────────
// '딜 취소' 버튼 → 확인 + 사유(수동 5종) 필수 선택 → cancelDeal.
export interface DealCancelModalState {
  open:   boolean;
  reason: string | null;   // 선택된 사유 (DEAL_CANCEL_REASONS_MANUAL 중)
  saving: boolean;
  error:  string;
}
export function emptyDealCancelModal(): DealCancelModalState {
  return { open: false, reason: null, saving: false, error: '' };
}

// ─── 구성 편집 모드 state (E2) ─────────────────────────────────────
//
// '구성 수정' 진입 시 활성 구성을 드래프트로 클론 → 편집 → '완료' 일괄 커밋.
// id 있는 행=기존 품목, 없으면 신규. 취소 시 폐기.
export interface EditDraftItem {
  lineId:              string;          // 클라 임시 식별자
  id?:                 string;          // 기존 deal_item id (신규면 undefined)
  category:            string | null;
  option_part:         string | null;
  item_description:    string | null;
  quantity:            string;          // input 문자열
  requested_condition: string | null;
  sell_unit_price_krw: number | null;
  is_unregistered:     boolean;         // 카탈로그 미등록 (§4.5)
  is_pending?:         boolean;         // catalog_part_pending(pending)에만 존재 → '임시등록'
  /**
   * 비하드웨어(마이그 145) — 표시 전용. 컨디션·소스 칸을 잠그는 근거다.
   * 서버가 마스터 매칭으로 계산해 내려준다(deal_item 컬럼이 아니다).
   */
  is_non_hw?:          boolean;
  description_raw:     string | null;   // 카탈로그 원문(표시 전용) — 등록 파트는 읽기전용 표시
  externalAdded?:      boolean;         // 409 병합으로 편입된 외부(봇) 추가 행 — 하이라이트 표시
  /**
   * 소스 스냅샷 — **표시 전용 carry**. 드래프트가 저장하는 값이 아니다.
   *
   * 소스는 '완료' 를 기다리지 않고 select 에서 즉시 PATCH 된다(편집 세션과
   * 주기가 다르다 — RFQ 응답을 보고 고르는 값이다). 그런데 표는 드래프트를
   * 그리므로, 저장된 선택을 다시 보여주려면 여기 실려 있어야 한다.
   *
   * dirty 판정(editConfigDirty)은 필드를 **명시 목록**으로 비교하므로 이 둘은
   * 들어가지 않는다 — 소스를 바꿔도 '저장/취소' 가 뜨지 않는다(의도).
   * 저장 성공 시 items 와 baseline 을 함께 갱신한다.
   */
  source_type:         string | null;
  source_ref_id:       string | null;
}
export interface EditConfigState {
  configId:   string;
  configName: string;
  items:      EditDraftItem[];
  saving:     boolean;
  isNew:      boolean;     // '새 구성'으로 막 만든 구성

  // 마지막 저장 상태 스냅샷 — dirty 판정(노란 border·저장/취소 노출) + '취소' 복원 기준.
  //   편집 진입/저장 직후 = items 의 깊은 복사. 이후 편집으로 items 가 달라지면 dirty.
  baseline:   EditDraftItem[];

  // ── 낙관적 잠금 기준 스냅샷 (마이그 114 — 동시성_설계 §3-1) ──
  // 편집 진입 시점의 deal.version + 이 구성의 item id 집합.
  // "외부 추가"(스냅샷에 없던 새 id)와 "사용자가 드래프트에서 지운 것"을 구분하는 근거.
  baseVersion: number;
  baseItemIds: string[];
  // 409 병합 후 배너 문구 — "저장하지 않았습니다 — 봇이 X 를 추가했습니다..."
  conflict:    string | null;
}

// ── 편집 dirty 판정 (미확정 구성 항상 편집 모드 — 저장/취소·노란 border 근거) ──
export type EditDirtyField = 'option_part' | 'item_description' | 'requested_condition' | 'quantity';
const _norm = (v: unknown): string => (v == null ? '' : String(v));

/** 한 행의 특정 필드가 baseline 대비 변경됐는지. base 없으면(신규 행) 항상 변경으로. */
export function editItemFieldDirty(
  cur: EditDraftItem, base: EditDraftItem | undefined, field: EditDirtyField,
): boolean {
  if (!base) return true;
  return _norm(cur[field]) !== _norm(base[field]);
}

/** 구성 전체가 baseline 대비 변경됐는지 (행 추가/삭제/필드 변경 포함). */
export function editConfigDirty(items: EditDraftItem[], baseline: EditDraftItem[]): boolean {
  if (items.length !== baseline.length) return true;
  const baseById = new Map(baseline.map((b) => [b.lineId, b]));
  const curIds   = new Set(items.map((i) => i.lineId));
  for (const b of baseline) if (!curIds.has(b.lineId)) return true;   // 삭제됨
  for (const it of items) {
    const b = baseById.get(it.lineId);
    if (!b) return true;                                              // 신규/치환
    if (editItemFieldDirty(it, b, 'option_part')
      || editItemFieldDirty(it, b, 'item_description')
      || editItemFieldDirty(it, b, 'requested_condition')
      || editItemFieldDirty(it, b, 'quantity')
      || _norm(it.sell_unit_price_krw) !== _norm(b.sell_unit_price_krw)
      || _norm(it.is_unregistered) !== _norm(b.is_unregistered)
      || _norm(it.category) !== _norm(b.category)) return true;
  }
  return false;
}

// 카탈로그 검색 패널 state — 파트넘버/디스크립션 교체 또는 신규 추가.
export interface ItemSearchState {
  open:         boolean;
  targetLineId: string;                 // 드래프트 행 lineId
  query:        string;
  loading:      boolean;
  hits:         api.CatalogSearchHit[];
  selectedKey:  string;                 // `${source}::${option_part}`
  stockOnly:    boolean;                // '재고 있는 품목만 검색' (§4.5.3, 기본 off)
}

// ─── 견적 보기 모달 state (P9-UI Step 3c) ──────────────────────────
//
// 견적 행의 [보기] 클릭 시 fetchQuoteFull 결과를 보관 + 표시.
export interface QuoteViewModalState {
  open:     boolean;
  loading:  boolean;
  error:    string;
  data:     api.QuoteFull | null;
  /** 레퍼런스 "더보기" 팝오버가 열린 행 index (편집 모달 refPopRow 과 같은 규약). */
  refPopRow: number | null;
}

// ─── 견적서 다운로드 종류 선택 모달 ────────────────────────────────
//
// **서버 품목이 포함된 견적만** 뜬다. 파트만 있으면 팝업 없이 바로 받는다 —
// 고를 게 없는데 한 단계를 더 두면 그냥 성가시다.
export interface QuoteDownloadModalState {
  open:     boolean;
  quoteId:  string | null;
  /** 모달 설명줄에 쓸 견적명. */
  quoteName: string;
  /** 다운로드 진행 중인 preset (버튼 중복 클릭 방지). */
  busy:     'server-full' | 'server-brief' | null;
  error:    string;
}

// ─── 견적 생성 모달 state (P9-UI Step 3b) ──────────────────────────
//
// 모달 안의 input 값을 들고 있는 state. 모달 닫을 때마다 reset.
// open / close 는 hidden 클래스 토글로 함.
export interface ContactEditState {
  mode:     'edit' | 'create';   // edit=기존 담당자 수정, create=신규 등록
  name:     string;
  position: string;
  email:    string;
  phone:    string;
  saving:   boolean;
  error:    string;
}

export interface CustomerSearchState {
  open:      boolean;              // 결과 드롭다운 표시
  query:     string;
  searching: boolean;
  hits:      api.CustomerRow[];
  saving:    boolean;             // 신규 등록/변경 진행중
}

export interface CustomerEditState {     // 현재 거래처 정보 직접 수정 (오등록 교정)
  name:        string;
  business_no: string;
  saving:      boolean;
  error:       string;
}

// ─── 딜 상세 거래처/담당자 지정 모달 (2026-07-14 스펙) ──────────────────
// 요약 + '변경' → 모달(검색·신규 등록). 딜 상세에선 레코드 편집 안 함(연결만).
export interface DealCustomerModalState {
  open:        boolean;
  query:       string;
  searching:   boolean;
  hits:        api.Company[];       // fetchCompanyList({role:'customer'}) 결과 (contact_count 포함)
  createOpen:  boolean;             // '+ 신규 거래처 등록' 폼 표시
  newName:     string;
  saving:      boolean;             // 등록/지정 진행중
  error:       string;
  confirmMode: boolean;             // draft 컨펌 진입(현재 거래처 승격 + 기존 재지정) — 마이그 139
  confirmName: string;              // 컨펌 대상 draft 거래처명(표시용)
}
export function emptyDealCustomerModal(): DealCustomerModalState {
  return { open: false, query: '', searching: false, hits: [], createOpen: false, newName: '', saving: false, error: '', confirmMode: false, confirmName: '' };
}

export interface DealContactModalState {
  open:        boolean;
  companyName: string;              // 부제 표시용(현재 거래처명)
  query:       string;              // 클라이언트 검색어(회사 소속 담당자 내)
  loading:     boolean;
  contacts:    api.Contact[];       // 현재 거래처 소속 담당자(로드)
  createOpen:  boolean;             // (미사용) 검색·신규 한 화면 전환 후 남겨둠
  newName:     string;
  newPosition: string;
  newEmail:    string;
  newPhone:    string;
  newAffiliation: string;   // 소속(부서/팀)
  newOfficePhone: string;   // 사무실번호(원본 유지)
  newNotes:       string;   // 메모
  saving:      boolean;
  error:       string;
  confirmMode: boolean;             // draft 컨펌 진입(현재 담당자 승격 + 기존 재지정) — 마이그 139
  confirmName: string;              // 컨펌 대상 draft 담당자명(표시용)
}
export function emptyDealContactModal(): DealContactModalState {
  return { open: false, companyName: '', query: '', loading: false, contacts: [], createOpen: false, newName: '', newPosition: '', newEmail: '', newPhone: '', newAffiliation: '', newOfficePhone: '', newNotes: '', saving: false, error: '', confirmMode: false, confirmName: '' };
}

export interface QuoteModalState {
  open:          boolean;
  editId:        string | null;   // null = 신규 작성, 값 = 수정 대상 quote id
  name:          string;          // 구성명 (예: "구성 1")
  fxRate:        string;          // 환율 KRW/USD (기본 1380, 영업담당자 입력)
  vatIncluded:   boolean;         // 부가세 포함 여부 (기본 false — 별도)
  items:         QuoteModalRow[]; // 각 deal_item 행
  /**
   * 펼친 서버 헤더행의 deal_item.id 집합. 서버는 **기본 접힘**(QUOTE-009).
   * 모달 안의 재계산·재렌더에는 유지되고, 모달을 닫으면 초기화된다
   * (모달 생애주기 스코프 — 그래서 뷰 전역 state 가 아니라 여기 산다).
   */
  expandedServers: Set<string>;
  /**
   * 견적 유형 — 마이그 178. **견적 단위**라 한 견적 안에서 판매와 임대가
   * 섞이지 않는다. 토글을 꺼도 라인의 임대 입력은 지우지 않는다(숨기기만) —
   * 잘못 눌렀을 때 값이 날아가면 안 된다. 저장할 때 유형에 맞는 쪽만 보낸다.
   */
  quoteType:     'SALE' | 'RENTAL';
  /**
   * 임대 계산식 프리셋 목록. 모달을 열 때 한 번 받아 둔다.
   * 로딩 실패해도 판매 견적은 그대로 돌아야 하므로 빈 배열이 기본이다.
   */
  presets:       RentalPreset[];
  presetsLoading: boolean;
  presetsError:  string;
  /**
   * 임대총원가 ⓘ 팝오버가 열린 행 index. 닫혀 있으면 null.
   *
   * 행마다 열어 두지 않고 **한 번에 하나만** 연다 — 여러 개가 겹치면 어느
   * 행의 내역인지 읽히지 않는다. 값이 index 인 이유는 임대 표가 이미
   * data-row-index 로 행을 가리키기 때문이다(계산식·기간·단가와 같은 규약).
   */
  costPopRow:    number | null;
  /**
   * 레퍼런스 "더보기" 팝오버가 열린 행 index. 닫혀 있으면 null.
   * costPopRow 와 같은 규약 — 한 번에 하나만 연다.
   */
  refPopRow:     number | null;
  saving:        boolean;
  errorMessage:  string;
  // 낙관적 잠금 (마이그 114) — 수정 모달 연 시점의 deal.version.
  // 409 시 refetch + 드래프트 유지 + errorMessage 안내 후 이 값을 최신으로 갱신.
  baseVersion:   number;
}

export interface QuoteModalRow {
  id:              string | null;       // 기존 quote_item id (수정 시) — 신규는 null
  deal_item_id:    string | null;
  /**
   * 서버 그룹 (마이그 131 / QUOTE-009).
   *   'server' = 서버 헤더행 — 품목칸은 모델명 한 줄, 소스·매입단가는 `-`,
   *              판매단가는 번들가 input, 수량은 대수.
   *   'part'   = 개별 품목 또는 서버 구성품(parentDealItemId 로 구분).
   */
  lineType:          'server' | 'part';
  /** 서버 구성품이면 부모 서버행의 **deal_item.id**(quote_item.id 아님). */
  parentDealItemId:  string | null;
  /** 서버 헤더행의 대수. 그 외 null. 읽기 전용(QUOTE-008). */
  serverQuantity:    number | null;
  /** 서버 구성품의 견적 카테고리(CHASSIS/CPU/…). 품목칸 앞에 붙는다. */
  specCategory:      string | null;
  category:        string | null;
  option_part:     string | null;
  description:     string | null;
  /**
   * 카탈로그 원문. 서버 구성품이 **개별 행**으로 나올 때 쓴다 (2026-08-04).
   * `description` 은 서버 헤더면 약식 첫 줄, 구성품이면 약식이라 행 단위로는
   * 정보가 줄어든다 — '1Gb 4Port' 대신 원문을 보여 준다.
   */
  descriptionRaw:  string | null;
  condition:       string | null;       // 표시용 (requested_condition)
  // 입력값 — string 으로 보관 (빈값 표현)
  quantity:        string;
  unit_price_krw:  string;   // 판매단가(개당) — 직접 입력. 저장/합계용. (마진율은 역산·표시만)
  // 기준 원가 USD — 레거시(원가 직접입력 폐기). 소스 스냅샷으로 대체.
  cost_basis_usd:  number | null;
  // 소스(원가 결정) — 마이그 105. 소스 선택 시 cost_basis 스냅샷.
  sourceType:          string | null;   // 'deal_rfq' | 'stock' | null
  sourceRefId:         string | null;   // deal_rfq → rfq_id
  costBasisAmount:     string;          // native 금액(문자열). '' = 없음
  costBasisCurrency:   string | null;   // 'USD' | 'KRW'
  /**
   * 섀시 기본 구성에서 담긴 품목인가 — 마이그 174.
   * TRUE 면 `기본` 뱃지 + 소스·매입단가 잠금(원가 0, 섀시 값에 포함).
   */
  isBasePart:          boolean;
  /** 약식 미적용(정식 카탈로그에 없어 폴백) — 노란 경고 표시용. */
  isUnregistered:      boolean;
  isPending:           boolean;
  // ─── 임대 (마이그 178/179) ────────────────────────────────────────
  /**
   * 서버 헤더행의 신품/중고. `condition` 은 '신품 서버' 같은 **표시 문구**라
   * 프리셋 매칭에 쓰면 문구를 바꾸는 순간 매칭이 깨진다. 저장값을 따로 든다.
   */
  serverCondition:     'NEW' | 'USED' | null;
  /** 임대기간 N (개월). 시트 E열 — 라인마다 다를 수 있다. */
  rentalTermMonths:    string;
  /** 고른 프리셋 id. '' 이면 미선택(서버 라인은 default_for 로 초기값이 잡힌다). */
  rentalPresetId:      string;
  /** 수동 임대단가 — 시트 R열. '' 이면 자동 계산. */
  rentalManualUnitKrw: string;
}

// ─── 약식 미적용 경고 (정식 카탈로그 미등록/임시 품목) ────────────────
// 개발로 약식을 자동 생성할 수 없는 품목(정식 카탈로그에 스펙 없음)은 디스크립션이
// 원문으로 폴백된다. 영업담당자가 규칙대로 수기 수정하도록 노란 경고를 띄운다.
//
// ★ on/off 스위치 — 임시 기능. 정식 카탈로그 등록이 충분히 진행되면 아래를 false 로 바꾸면
//   딜 약식·견적 모달 모든 곳의 '약식 미적용' 경고가 한 번에 사라진다(품목의 임시/미등록 칩은 별개).
export const SHORTHAND_FIX_WARN_ENABLED = true;
export function needsShorthandFix(
  x: { isUnregistered?: boolean | null; isPending?: boolean | null; is_unregistered?: boolean | null; is_pending?: boolean | null },
): boolean {
  return !!(x.isUnregistered ?? x.is_unregistered) || !!(x.isPending ?? x.is_pending);
}
// 경고에 표시할 품목 라벨 — 파트넘버 우선, 없으면 카테고리+설명 앞부분.
export function shorthandItemLabel(x: {
  option_part?: string | null;
  spec_category?: string | null; specCategory?: string | null; category?: string | null;
  description?: string | null; item_description?: string | null; description_raw?: string | null;
}): string {
  const cat = (x.spec_category ?? x.specCategory ?? x.category ?? '').trim();
  const pn = (x.option_part ?? '').trim();
  if (pn) return [cat, pn].filter(Boolean).join(' ');
  const desc = (x.description ?? x.item_description ?? x.description_raw ?? '').trim().split('\n')[0].slice(0, 40);
  return [cat, desc].filter(Boolean).join(' ') || '(품목)';
}
export function shorthandFixWarningHtml(labels: string[] = []): string {
  if (!SHORTHAND_FIX_WARN_ENABLED) return '';
  const list = labels.filter(Boolean);
  const body = list.length
    ? '약식 적용 안됨 — 아래 품목의 디스크립션을 규칙에 맞게 수기로 수정해서 사용해주세요: <b>' + list.map(escapeHtml).join(', ') + '</b>'
    : '약식 적용 안됨 — 해당 품목의 디스크립션을 규칙에 맞게 수기로 수정해서 사용해주세요.';
  return '<span class="deal-shorthand-warn">\u26A0 ' + body + '</span>';
}
// 견적 표의 개별 품목 행용 — 한 줄 압축 배지(품목은 그 행에 이미 보이므로 라벨 생략).
export function shorthandFixCompactHtml(): string {
  if (!SHORTHAND_FIX_WARN_ENABLED) return '';
  return '<span class="deal-shorthand-warn deal-shorthand-warn--compact" title="해당 품목의 디스크립션을 규칙에 맞게 수기로 수정해서 사용해주세요.">\u26A0 약식 미적용 · 디스크립션 수기 수정필요</span>';
}

// ─── 도메인 상수 ────────────────────────────────────────────────────
// 품목 검색 hit 의 `source` 가 이 목록에 있어야 deal_item.category 로 들어간다
// (DealDetailView.handleItemSearchPick). 카탈로그 소스명과 같은 어휘다.
//   ★ `non_hw` 는 마이그 145(비하드웨어 마스터) 때 빠져 있었다 — 배송비를 담아도
//     category 가 조용히 안 들어갔다. 같은 마이그가 놓친 화이트리스트가 셋이다:
//     ADMIN_TABLES / search-helpers / 여기.
export const ITEM_CATEGORY_OPTIONS = [
  'chassis', 'cpu', 'gpu', 'memory', 'ssd', 'hdd',
  'network_adapter', 'hba', 'raid_controller', 'power_supply',
  'pre_configured_model', 'non_hw', 'other',
] as const;

// STATUS_ORDER — DealDetailDrawer 와 동일
export const STATUS_ORDER: api.DealStatus[] = [
  '리드 접수됨',
  '리드 검토됨',
  '소싱 확인중',
  '견적 작성 완료',
  '견적 발송 완료',
  '수주',
  '매입 진행중',
  '세팅중',
  '세팅 완료',
  '납품 완료',
  '취소',
];

// 유입 경로 옵션 (admin-ux-preview 기준)
export const LEAD_SOURCE_OPTIONS = [
  '기존 거래처',
  '신규 거래처 - 소개',
  '신규 거래처 - 웹사이트',
  '신규 거래처 - 블로그',
  '신규 거래처 - 직접 영업',
  '기타',
] as const;

/** 문의분류 옵션 (§4.2.8 선택 UI) — 프로토타입 순서. */
export const CLASSIFICATION_OPTIONS: readonly api.DealClassification[] = ['파트 구매', '파트 임대', '서버 구매', '서버 임대'];

/**
 * 수동 딜 취소 사유(기획 5종) — UI 드롭다운 노출용.
 * 백엔드 enum(DEAL_CANCEL_REASONS)은 봇/자동취소 값까지 포함한 superset이지만,
 * 사람이 직접 고르는 목록은 이 5개만. (STATUS_ORDER 처럼 프론트 로컬 정본)
 */
export const DEAL_CANCEL_REASONS_MANUAL = [
  '고객 취소', '견적 만료', '견적 불가', '견적 미진행', '담당자 취소',
] as const;

// ─── 공용 포맷 헬퍼 ─────────────────────────────────────────────────
// view(품목 편집 input)와 render-quote 양쪽에서 사용 — 순환 import 방지 위해 여기에.
/** 숫자 → 3자리 콤마 (판매 단가). 빈값/NaN 은 빈 문자열. */
export function fmtThousands(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '';
  return n.toLocaleString('ko-KR');
}

// ─── factory — 빈 state 생성 ────────────────────────────────────────
let lineIdSeq = 0;
export function nextLineId(): string {
  lineIdSeq += 1;
  return `line-${Date.now()}-${lineIdSeq}`;
}

export function emptyItemSearchState(): ItemSearchState {
  return { open: false, targetLineId: '', query: '', loading: false, hits: [], selectedKey: '', stockOnly: false };
}

export interface NeedEditState {
  open:        boolean;
  needId:      string;
  partNumber:  string;   // 표시용(읽기)
  description: string;   // 표시용(읽기)
  quantity:    string;   // 입력
  condition:   string;   // dropdown
  saving:      boolean;
  error:       string;
}
export function emptyNeedEdit(): NeedEditState {
  return { open: false, needId: '', partNumber: '', description: '', quantity: '', condition: '', saving: false, error: '' };
}

export function emptyNeedCreate() {
  return {
    open: false, query: '', searching: false, hits: [] as api.CatalogSearchHit[],
    option_part: '', item_text: '', category: null as string | null,
    condition: '', quantity: '1', salesTags: [] as string[],
    dealLinked: true, saving: false,
    sourceDealItemId: null as string | null,
  };
}

export interface ServerAddState {
  open:      boolean;
  /** 'pick' = 두 갈래 선택, 'import' = 기존 구성 검색. */
  mode:      'pick' | 'import';
  /** 어느 구성에 붙일지. 모달을 연 버튼이 알려준다. */
  configId:  string | null;
  query:     string;
  searching: boolean;
  hits:      api.SavedServerItem[];
  /** 다음 페이지 존재 — '더 보기' 노출 조건. */
  hasMore:   boolean;
  /** 다음 페이지 시작 위치(= 지금까지 받은 건수). */
  offset:    number;
  saving:    boolean;
  error:     string;
}

export function emptyServerAdd(): ServerAddState {
  return {
    open: false, mode: 'pick', configId: null,
    query: '', searching: false, hits: [], hasMore: false, offset: 0,
    saving: false, error: '',
  };
}

export function emptyQuoteModalState(): QuoteModalState {
  return {
    open:         false,
    editId:       null,
    name:         '',
    fxRate:       '1380',
    vatIncluded:  false,
    items:        [],
    expandedServers: new Set<string>(),
    quoteType:    'SALE',
    presets:      [],
    presetsLoading: false,
    presetsError: '',
    costPopRow:   null,
    refPopRow:    null,
    saving:       false,
    errorMessage: '',
    baseVersion:  0,
  };
}

// QuoteItem(DB) → 수정 모달 행. id 를 실어 diff 가능하게.
// (fx: 옛 마진율 역산에 쓰였으나 판매단가 직접입력 전환 후 미사용 — 시그니처만 유지)
export function quoteItemToModalRow(it: api.QuoteItem, _fx = 0): QuoteModalRow {
  const q = it as api.QuoteItem & { is_unregistered?: boolean | null; is_pending?: boolean | null };
  const row: QuoteModalRow = {
    id:             it.id,
    deal_item_id:   it.deal_item_id,
    category:       it.category,
    option_part:    it.option_part,
    description:    it.description,
    // quote_item 은 카탈로그 원문을 들고 있지 않다(저장 시점 스냅샷만).
    // 서버 구성품 행의 원문 표시는 딜 품목 경로에서만 채워진다.
    descriptionRaw: null,
    condition:      null,   // quote_item 에 컨디션 없음(표시용) — 비움
    quantity:       it.quantity != null ? String(it.quantity) : '',
    unit_price_krw: it.unit_price_krw != null ? String(it.unit_price_krw) : '',
    cost_basis_usd: it.cost_basis_usd,
    sourceType:        it.source_type,
    sourceRefId:       it.source_ref_id,
    costBasisAmount:   it.cost_basis_amount != null ? String(it.cost_basis_amount) : '',
    costBasisCurrency: it.cost_basis_currency,
    // 서버 그룹 복원 — 저장된 견적을 다시 열 때 트리가 풀리지 않게 한다.
    lineType:         it.line_type === 'server' ? 'server' : 'part',
    parentDealItemId: it.parent_deal_item_id ?? null,
    serverQuantity:   it.server_quantity ?? null,
    specCategory:     null,   // quote_item 에는 spec_category 가 없다(카테고리는 category 칸)
    // 마이그 174 — quote_item 컬럼이 아니라 deal_item 조인 유도값이다.
    isBasePart:       it.is_base_part ?? false,
    isUnregistered:   q.is_unregistered ?? false,
    isPending:        q.is_pending ?? false,
    // 임대 복원 — quote_item 에 server_condition 은 없다(딜 품목 조인 값이라).
    //   프리셋은 저장된 id 를 그대로 쓰므로 컨디션 추론이 필요 없다.
    serverCondition:     null,
    rentalTermMonths:    it.rental_term_months != null ? String(it.rental_term_months) : '',
    rentalPresetId:      it.rental_preset_id ?? '',
    rentalManualUnitKrw: it.rental_manual_unit_krw != null ? String(it.rental_manual_unit_krw) : '',
  };
  // 서버 헤더행의 판매단가·수량은 전용 칸에 저장돼 있다.
  if (row.lineType === 'server') {
    row.unit_price_krw = it.unit_price_override != null ? String(it.unit_price_override) : '';
    row.quantity       = String(it.server_quantity ?? 1);
  }
  return row;
}

export function emptyQuoteViewModalState(): QuoteViewModalState {
  return {
    open:    false,
    loading: false,
    error:   '',
    data:    null,
    refPopRow: null,
  };
}

export function emptyQuoteDownloadModal(): QuoteDownloadModalState {
  return { open: false, quoteId: null, quoteName: '', busy: null, error: '' };
}

// useSellPrice=true (바로 견적): 품목 판매 단가를 견적 단가(unit_price_krw)로 prefill.
export function dealItemToModalRow(item: api.DealItem, useSellPrice = false): QuoteModalRow {
  const isServer = item.line_type === 'server';
  return {
    id:             null,
    deal_item_id:   item.id,
    lineType:         isServer ? 'server' : 'part',
    parentDealItemId: item.parent_id ?? null,
    serverQuantity:   isServer ? (item.server_quantity ?? 1) : null,
    specCategory:     item.spec_category ?? null,
    category:       item.category ?? null,
    option_part:    item.option_part ?? null,
    // 서버 헤더행의 품목칸은 **모델명 한 줄**이다(QUOTE-009).
    //   2026-08-21 부터 item_description 자체가 제목 한 줄이라 split 이 사실상
    //   불필요하지만 그대로 둔다 — 옛 데이터(요약 8줄이 저장된 행)가 남아 있고,
    //   그때도 첫 줄이 섀시 모델이라는 게 shorthand 생성기의 계약이다.
    // 구성품은 약식 디스크립션(item_description)이 정본 — 카탈로그 원문이 아니다.
    // 서버 헤더행은 약식 요약의 첫 줄(모델명). 그건 한 줄로 접는 자리라 약식이 맞다.
    // 구성품·개별 파트는 **개별 행**이므로 카탈로그 원문이 정본이다
    //   (§2026-08-04 서안드레 — 서버 구성 표·딜 상세가 이미 그렇게 돈다).
    //   원문이 없으면(미등록·임시등록) 저장된 문구로 폴백한다.
    description:    isServer
      ? ((item.item_description ?? '').split('\n')[0] || null)
      : (item.description_raw ?? item.item_description ?? null),
    descriptionRaw: item.description_raw ?? null,
    // V2 구성품은 독립 컨디션을 갖지 않는다(ITEM-012). 서버 헤더는 신품/중고 서버.
    condition:      isServer
      ? (item.server_condition === 'NEW' ? '신품 서버'
        : item.server_condition === 'USED' ? '중고 서버' : '서버')
      : (item.parent_id != null ? null : (item.requested_condition ?? null)),
    // 서버 헤더의 '수량' = 대수. 구성품 수량은 서버 1대 기준 그대로.
    quantity:       isServer
      ? String(item.server_quantity ?? 1)
      : (item.quantity != null ? String(item.quantity) : ''),
    // 서버 헤더의 판매단가 = 번들가(unit_price_override). 자식 합계를 자동
    // 승격하지 않는다(마이그 146) — 비어 있으면 비운 채로 둔다.
    unit_price_krw: isServer
      ? (item.unit_price_override != null ? String(item.unit_price_override) : '')
      : (useSellPrice && item.sell_unit_price_krw != null ? String(item.sell_unit_price_krw) : ''),
    cost_basis_usd: null,
    // 딜에서 고른 소스 스냅샷을 그대로 이어받는다 — 견적에서 다시 고르지 않아도 된다.
    sourceType:        isServer ? null : (item.source_type ?? null),
    sourceRefId:       isServer ? null : (item.source_ref_id ?? null),
    costBasisAmount:   isServer || item.cost_basis_amount == null ? '' : String(item.cost_basis_amount),
    costBasisCurrency: isServer ? null : (item.cost_basis_currency ?? null),
    // 마이그 174 — 딜 품목의 표시를 그대로 견적으로 가져온다. 매입단가 0 은
    // 이미 deal_item 에 0 으로 저장돼 있어 위 costBasisAmount 로 따라온다.
    isBasePart:        !isServer && (item.is_base_part ?? false),
    isUnregistered:    !!(item as api.DealItem & { is_unregistered?: boolean|null }).is_unregistered,
    isPending:         !!(item as api.DealItem & { is_pending?: boolean|null }).is_pending,
    // 임대 — 서버 라인만 신품/중고를 안다. 프리셋 초기 선택은 렌더 시점에
    //   `defaultPresetIdFor` 가 잡으므로 여기서는 비워 둔다(프리셋 목록이
    //   아직 안 왔을 수 있다).
    serverCondition:     isServer && (item.server_condition === 'NEW' || item.server_condition === 'USED')
                           ? item.server_condition : null,
    rentalTermMonths:    '12',
    rentalPresetId:      '',
    rentalManualUnitKrw: '',
  };
}

// 빈 견적 행 — 항목 추가용. 품목/디스크립션은 사용자 입력.
export function emptyQuoteModalRow(): QuoteModalRow {
  return {
    id: null, deal_item_id: null, category: null, option_part: null, description: null,
    descriptionRaw: null, isBasePart: false, isUnregistered: false, isPending: false,
    condition: null, quantity: '1', unit_price_krw: '', cost_basis_usd: null,
    sourceType: null, sourceRefId: null, costBasisAmount: '', costBasisCurrency: null,
    // 손으로 추가한 행은 항상 평면 개별 품목이다(서버는 편집기에서만 만든다).
    lineType: 'part', parentDealItemId: null, serverQuantity: null, specCategory: null,
    // 임대 — 손으로 추가한 행은 서버가 아니므로 프리셋 자동 선택 대상이 아니다.
    serverCondition: null, rentalTermMonths: '12', rentalPresetId: '', rentalManualUnitKrw: '',
  };
}
